import {
  badgeVariants,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon, ListChecks } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { FileApprovalProgress as FileApprovalProgressData } from '../../model/types';
import {
  getFileApprovalLabel,
  getFileApprovalState,
  getFileApprovalAwaitingLabel,
  type FileApprovalState,
} from '../../lib/file-progress.utils';

interface Props {
  progress: FileApprovalProgressData;
}

const VARIANT_BY_STATE: Record<FileApprovalState, 'warning' | 'positive'> = {
  // A chain nobody has acted on yet is still a chain waiting on someone, and
  // the design draws it the same as one mid-flight rather than as neutral.
  not_started: 'warning',
  in_progress: 'warning',
  completed: 'positive',
};

const ICON_BY_STATE: Record<FileApprovalState, typeof CheckIcon> = {
  not_started: ListChecks,
  in_progress: ListChecks,
  completed: CheckIcon,
};

export function FileApprovalProgress(props: Props) {
  const { t } = useTranslation('projects');

  const { progress } = props;
  const state = getFileApprovalState(progress);
  const label = getFileApprovalLabel(progress);
  const awaitingLabel = getFileApprovalAwaitingLabel(progress);
  const Icon = ICON_BY_STATE[state];
  const chainName = t('assetApprovalChain.fileApprovalProgress.approvalChainName', {
    name: progress.workflowName,
  });

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Badge is a `span` and takes no `asChild`, so the tag borrows its
              recipe rather than its element: the trigger stays a real button so
              the chain name is reachable by keyboard as well as by pointer. The
              card underneath opens the asset, so the press stops here. */}
          <button
            type="button"
            aria-label={`${progress.workflowName}: ${label}`}
            className="flex min-w-0 cursor-default items-center gap-1 bg-transparent p-0 focus-visible:focus-ring"
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className={badgeVariants({
                variant: VARIANT_BY_STATE[state],
                size: 'sm',
                roundness: 'full',
                className: 'shrink-0',
              })}
            >
              <Icon className="size-3.5 shrink-0" />
              {label}
            </span>
            {awaitingLabel ? (
              <span className="block min-w-0 max-w-30 truncate text-body-sm text-muted-foreground">
                {awaitingLabel}
              </span>
            ) : null}
          </button>
        </TooltipTrigger>
        <TooltipContent align="start">{chainName}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
